/**
 * The brain's geometry, decoded from the files the Rust build step wrote.
 *
 * neurons.bin: one 24-byte record per neuron index, little-endian
 *     f32 x, y, z (nm) | u16 cell_type | u8 region | u8 side
 *     | u16 cell_class | u8 super_class | u8 flags | u32 reserved
 * ids.bin:     u64 root id per neuron index (0 for the kernel's padding slots)
 * edges_*.bin: u32 source, u32 target, i16 weight, parallel arrays
 *
 * Index space: the on-chain kernel addresses neurons by index, not by name. The
 * build step reproduces the exporter's order (unique root ids of the BANC v888
 * edge list, lexsorted by region rank then root id), so index -> 3D position is
 * a straight lookup.
 */

import { asArray, asNumber, asString, fetchJson, field } from "./json";
import type { JsonValue } from "./json";

export const RECORD_BYTES = 24;

export type BrainMeta = {
  readonly neurons: number;
  readonly slots: number;
  readonly regions: readonly string[];
  readonly sides: readonly string[];
  readonly cellClasses: readonly string[];
  readonly superClasses: readonly string[];
  readonly cellTypes: readonly string[];
  readonly regionCounts: ReadonlyMap<string, number>;
  readonly positionFromVoxel: number;
  readonly positionUnresolved: number;
};

export type NeuronGeometry = {
  readonly meta: BrainMeta;
  /** micron positions, xyz triples */
  readonly positions: Float32Array;
  readonly ids: BigUint64Array;
  readonly regions: Uint8Array;
  readonly sides: Uint8Array;
  readonly cellClasses: Uint16Array;
  readonly cellTypes: Uint16Array;
  /** BANC's super-class rank: the annotation the firing spectrum's stain varies with. */
  readonly superClasses: Uint8Array;
  readonly flags: Uint8Array;
};

export type Connectome = {
  readonly sources: Uint32Array;
  readonly targets: Uint32Array;
  readonly weights: Int16Array;
};

function stringList(source: JsonValue, key: string, label: string): readonly string[] {
  return asArray(field(source, key, label), `${label}.${key}`).map((entry, index) =>
    asString(entry, `${label}.${key}[${index}]`),
  );
}

export function decodeMeta(source: JsonValue): BrainMeta {
  const countsSource = field(source, "counts", "meta");
  const regionSource = field(countsSource, "region", "meta.counts");
  const regionNames = stringList(source, "regions", "meta");

  const counts = regionNames.map((name) =>
    asNumber(field(regionSource, name, "meta.counts.region"), name),
  );

  return {
    neurons: asNumber(field(source, "n_real", "meta"), "meta.n_real"),
    slots: asNumber(field(source, "n_slots", "meta"), "meta.n_slots"),
    regions: regionNames,
    sides: stringList(source, "sides", "meta"),
    cellClasses: stringList(source, "cell_classes", "meta"),
    superClasses: stringList(source, "super_classes", "meta"),
    cellTypes: stringList(source, "cell_types", "meta"),
    regionCounts: new Map(regionNames.map((name, index) => [name, counts[index]])),
    positionFromVoxel: asNumber(
      field(countsSource, "position_from_voxel", "meta.counts"),
      "position_from_voxel",
    ),
    positionUnresolved: asNumber(
      field(countsSource, "position_unresolved", "meta.counts"),
      "position_unresolved",
    ),
  };
}

export async function fetchMeta(): Promise<BrainMeta> {
  return decodeMeta(await fetchJson("/data/meta.json"));
}

async function fetchBuffer(url: string): Promise<ArrayBuffer> {
  const response = await fetch(url);

  if (!response.ok) throw new Error(`${url}: HTTP ${response.status}`);

  return response.arrayBuffer();
}

export async function fetchGeometry(meta: BrainMeta): Promise<NeuronGeometry> {
  const [neuronBytes, idBytes] = await Promise.all([
    fetchBuffer("/data/neurons.bin"),
    fetchBuffer("/data/ids.bin"),
  ]);

  const records = new DataView(neuronBytes);
  const slots = meta.slots;

  if (neuronBytes.byteLength !== slots * RECORD_BYTES) {
    throw new Error(
      `neurons.bin: ${neuronBytes.byteLength} bytes, expected ${slots * RECORD_BYTES}`,
    );
  }

  const positions = new Float32Array(slots * 3);
  const regions = new Uint8Array(slots);
  const sides = new Uint8Array(slots);
  const cellClasses = new Uint16Array(slots);
  const cellTypes = new Uint16Array(slots);
  const superClasses = new Uint8Array(slots);
  const flags = new Uint8Array(slots);

  for (let index = 0; index < slots; index += 1) {
    const at = index * RECORD_BYTES;
    // nm -> micron keeps the camera numbers readable; BANC's y grows posteriorly,
    // so it is flipped here to put the brain above the nerve cord on screen
    positions[index * 3] = records.getFloat32(at, true) / 1000;
    positions[index * 3 + 1] = -records.getFloat32(at + 4, true) / 1000;
    positions[index * 3 + 2] = records.getFloat32(at + 8, true) / 1000;
    cellTypes[index] = records.getUint16(at + 12, true);
    regions[index] = records.getUint8(at + 14);
    sides[index] = records.getUint8(at + 15);
    cellClasses[index] = records.getUint16(at + 16, true);
    superClasses[index] = records.getUint8(at + 18);
    flags[index] = records.getUint8(at + 19);
  }

  return {
    meta,
    positions,
    ids: new BigUint64Array(idBytes).subarray(0, slots),
    regions,
    sides,
    cellClasses,
    cellTypes,
    superClasses,
    flags,
  };
}

export async function fetchConnectome(): Promise<Connectome> {
  const [sourceBytes, targetBytes, weightBytes] = await Promise.all([
    fetchBuffer("/data/edges_src.bin"),
    fetchBuffer("/data/edges_tgt.bin"),
    fetchBuffer("/data/edges_w.bin"),
  ]);

  return {
    sources: new Uint32Array(sourceBytes),
    targets: new Uint32Array(targetBytes),
    weights: new Int16Array(weightBytes),
  };
}
